import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Settings, AlertTriangle, Bell, Save, Eye, 
  TrendingDown, TrendingUp, Activity, Zap, RefreshCw, Clock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ThresholdSlider = ({ label, value, onChange, min = 0, max = 100, step = 5, color, icon: Icon }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${color}`} />}
          <Label className="font-medium">{label}</Label>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={Math.round(value * 100)}
            onChange={(e) => onChange(parseInt(e.target.value) / 100)}
            className="w-20 text-right"
            min={min}
            max={max}
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
      </div>
      <Slider
        value={[value * 100]}
        onValueChange={([v]) => onChange(v / 100)}
        min={min}
        max={max}
        step={step}
        className="py-2"
      />
    </div>
  );
};

const AlertPreviewCard = ({ alert }) => {
  const severityColors = {
    critical: 'bg-red-100 text-red-700 border-red-300',
    warn: 'bg-amber-100 text-amber-700 border-amber-300',
    info: 'bg-blue-100 text-blue-700 border-blue-300'
  };

  const typeIcons = {
    underload: TrendingDown,
    low_load: TrendingDown,
    optimal: Activity,
    high_load: TrendingUp,
    burnout_risk: Zap
  };

  const Icon = typeIcons[alert.alert_type] || AlertTriangle;

  return (
    <div className={`p-3 rounded-lg border ${severityColors[alert.severity]}`}>
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{alert.user_name}</p>
          <p className="text-sm mt-1">{alert.message}</p>
          <div className="flex items-center gap-4 mt-2 text-xs opacity-80">
            <span>Utilizare: {(alert.utilization_pct * 100).toFixed(0)}%</span>
            <span>Ore lucrate: {alert.hours_worked.toFixed(1)}h</span>
            <span>Capacitate: {alert.capacity_hours.toFixed(0)}h</span>
          </div>
        </div>
        <Badge variant="outline" className="text-xs capitalize">
          {alert.alert_type.replace('_', ' ')}
        </Badge>
      </div>
    </div>
  );
};

export const AlertsConfig = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState({
    underload_threshold: 0.60,
    optimal_min: 0.70,
    optimal_max: 0.80,
    burnout_threshold: 0.90,
    persistence_days: 3,
    alert_on_state_change: true,
    default_daily_capacity_hours: 8,
    target_utilization: 0.75,
    buffer_percent: 0.10
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMonth, setPreviewMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/admin/alerts-config');
      setConfig(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea configurației');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/admin/alerts-config', config);
      toast.success('Configurația a fost salvată!');
    } catch (err) {
      toast.error('Eroare la salvarea configurației');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post(`/admin/alerts-preview?month=${previewMonth}`, config);
      setPreview(res.data);
    } catch (err) {
      toast.error('Eroare la generarea preview-ului');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="w-7 h-7 text-blue-500" />
              Configurare Alerte
            </h1>
            <p className="text-muted-foreground">
              Setează pragurile pentru alertele de utilizare și capacity planning
            </p>
          </div>
          <Button 
            onClick={saveConfig} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvează
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Thresholds Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Praguri Utilizare
              </CardTitle>
              <CardDescription>
                Definește intervalele pentru alertele de încărcare
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Visual Scale */}
              <div className="relative h-8 rounded-full overflow-hidden bg-slate-100">
                <div 
                  className="absolute left-0 top-0 h-full bg-red-400"
                  style={{ width: `${config.underload_threshold * 100}%` }}
                />
                <div 
                  className="absolute top-0 h-full bg-yellow-400"
                  style={{ 
                    left: `${config.underload_threshold * 100}%`,
                    width: `${(config.optimal_min - config.underload_threshold) * 100}%`
                  }}
                />
                <div 
                  className="absolute top-0 h-full bg-green-400"
                  style={{ 
                    left: `${config.optimal_min * 100}%`,
                    width: `${(config.optimal_max - config.optimal_min) * 100}%`
                  }}
                />
                <div 
                  className="absolute top-0 h-full bg-orange-400"
                  style={{ 
                    left: `${config.optimal_max * 100}%`,
                    width: `${(config.burnout_threshold - config.optimal_max) * 100}%`
                  }}
                />
                <div 
                  className="absolute top-0 h-full bg-red-500"
                  style={{ 
                    left: `${config.burnout_threshold * 100}%`,
                    width: `${(1 - config.burnout_threshold) * 100}%`
                  }}
                />
              </div>

              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0%</span>
                <span className="text-red-500">Underload</span>
                <span className="text-yellow-600">Low</span>
                <span className="text-green-600">Optimal</span>
                <span className="text-orange-500">High</span>
                <span className="text-red-600">Burnout</span>
                <span>100%</span>
              </div>

              <div className="space-y-6 pt-4">
                <ThresholdSlider
                  label="Underload (sub acest prag)"
                  value={config.underload_threshold}
                  onChange={(v) => setConfig({ ...config, underload_threshold: v })}
                  color="text-red-500"
                  icon={TrendingDown}
                />
                
                <ThresholdSlider
                  label="Optim minim"
                  value={config.optimal_min}
                  onChange={(v) => setConfig({ ...config, optimal_min: v })}
                  color="text-green-500"
                  icon={Activity}
                />
                
                <ThresholdSlider
                  label="Optim maxim"
                  value={config.optimal_max}
                  onChange={(v) => setConfig({ ...config, optimal_max: v })}
                  color="text-green-500"
                  icon={Activity}
                />
                
                <ThresholdSlider
                  label="Risc Burnout (peste acest prag)"
                  value={config.burnout_threshold}
                  onChange={(v) => setConfig({ ...config, burnout_threshold: v })}
                  color="text-red-600"
                  icon={Zap}
                />
              </div>
            </CardContent>
          </Card>

          {/* Capacity Planning Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Capacity Planning
              </CardTitle>
              <CardDescription>
                Setări implicite pentru planificarea capacității
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Ore zilnice standard</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={config.default_daily_capacity_hours}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        default_daily_capacity_hours: parseFloat(e.target.value) 
                      })}
                      className="w-20 text-right"
                      min={1}
                      max={24}
                    />
                    <span className="text-sm text-muted-foreground">ore</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Implicit pentru toți angajații, poate fi suprascris per persoană
                </p>
              </div>

              <ThresholdSlider
                label="Target Utilizare"
                value={config.target_utilization}
                onChange={(v) => setConfig({ ...config, target_utilization: v })}
                color="text-blue-500"
                min={50}
                max={95}
              />

              <ThresholdSlider
                label="Buffer pentru urgențe"
                value={config.buffer_percent}
                onChange={(v) => setConfig({ ...config, buffer_percent: v })}
                color="text-purple-500"
                min={0}
                max={30}
              />

              <div className="pt-4 border-t space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Persistență alerte</Label>
                    <p className="text-xs text-muted-foreground">
                      Alert doar dacă starea persistă N zile
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={config.persistence_days}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        persistence_days: parseInt(e.target.value) 
                      })}
                      className="w-16 text-right"
                      min={1}
                      max={30}
                    />
                    <span className="text-sm text-muted-foreground">zile</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Alert la schimbare stare</Label>
                    <p className="text-xs text-muted-foreground">
                      Trimite alert când se schimbă statusul
                    </p>
                  </div>
                  <Switch
                    checked={config.alert_on_state_change}
                    onCheckedChange={(v) => setConfig({ 
                      ...config, 
                      alert_on_state_change: v 
                    })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Preview Alerte
                </CardTitle>
                <CardDescription>
                  Cum ar arăta alertele cu configurația curentă
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="month"
                  value={previewMonth}
                  onChange={(e) => setPreviewMonth(e.target.value)}
                  className="w-40"
                />
                <Button 
                  onClick={loadPreview}
                  disabled={previewLoading}
                  variant="outline"
                >
                  {previewLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Generează Preview
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {preview ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-5 gap-4">
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">
                        {preview.summary.underload}
                      </p>
                      <p className="text-xs text-muted-foreground">Underload</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-600">
                        {preview.summary.low_load}
                      </p>
                      <p className="text-xs text-muted-foreground">Low Load</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {preview.summary.optimal}
                      </p>
                      <p className="text-xs text-muted-foreground">Optimal</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {preview.summary.high_load}
                      </p>
                      <p className="text-xs text-muted-foreground">High Load</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-700">
                        {preview.summary.burnout_risk}
                      </p>
                      <p className="text-xs text-muted-foreground">Burnout Risk</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Alert List */}
                {preview.alerts.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {preview.alerts.map((alert) => (
                      <AlertPreviewCard key={alert.id} alert={alert} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Toți angajații sunt în zona optimă!</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Selectează o lună și apasă "Generează Preview" pentru a vedea alertele</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fix Estimated Times Tool */}
        <FixEstimatedTimesTool />
      </div>
    </Layout>
  );
};

// Admin tool to fix estimated times
const FixEstimatedTimesTool = () => {
  const [report, setReport] = useState(null);
  const [fixResult, setFixResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/estimated-times-report');
      setReport(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea raportului');
    } finally {
      setLoading(false);
    }
  };

  const runAutoFix = async () => {
    if (!window.confirm('Ești sigur? Taskurile cu timp > 60min vor fi corectate automat.')) return;
    setFixing(true);
    try {
      const res = await api.post('/admin/fix-estimated-times');
      setFixResult(res.data);
      toast.success(res.data.message);
      loadReport(); // Refresh report
    } catch (err) {
      toast.error('Eroare la corectare');
    } finally {
      setFixing(false);
    }
  };

  const fixSingle = async (taskId, minutes) => {
    try {
      await api.post(`/admin/fix-task-time/${taskId}?minutes=${minutes}`);
      toast.success(`Task corectat la ${minutes}min`);
      loadReport();
    } catch (err) {
      toast.error('Eroare');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-500" />
          Corectare Timpuri Estimate
        </CardTitle>
        <CardDescription>
          Verifică și corectează taskurile cu timp estimat incorect (bug dublu-contorizare)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={loadReport} disabled={loading} variant="outline">
            {loading ? 'Se încarcă...' : 'Vezi Raport'}
          </Button>
          <Button onClick={runAutoFix} disabled={fixing} className="bg-orange-500 hover:bg-orange-600">
            {fixing ? 'Se corectează...' : 'Corectare Automată'}
          </Button>
        </div>

        {fixResult && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
            <p className="font-medium text-green-700">{fixResult.message}</p>
            {fixResult.fixed?.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto">
                {fixResult.fixed.map((f, i) => (
                  <p key={i} className="text-xs text-green-600">
                    {f.title}: {f.old_estimated_minutes}min → {f.new_estimated_minutes}min
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {report && (
          <div>
            <p className="text-sm mb-2">
              Total: <strong>{report.total}</strong> taskuri | 
              Suspecte: <strong className="text-red-600">{report.suspicious_count}</strong>
            </p>
            {report.suspicious_count > 0 && (
              <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Task</th>
                      <th className="text-center p-2">Arată</th>
                      <th className="text-center p-2">Status</th>
                      <th className="text-center p-2">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.tasks.filter(t => t.suspicious).map((t) => (
                      <tr key={t.id} className="border-t hover:bg-red-50">
                        <td className="p-2">
                          <p className="font-medium text-xs">{t.title}</p>
                          <p className="text-[10px] text-muted-foreground">{t.project_name}</p>
                        </td>
                        <td className="p-2 text-center">
                          <span className="text-red-600 font-bold">{t.showing_as_minutes}min</span>
                        </td>
                        <td className="p-2 text-center text-xs">{t.status}</td>
                        <td className="p-2 text-center">
                          <div className="flex gap-1 justify-center">
                            {[15, 30, 45, 60].map(m => (
                              <Button key={m} variant="outline" size="sm" className="h-6 text-[10px] px-1.5"
                                onClick={() => fixSingle(t.id, m)}>
                                {m}min
                              </Button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsConfig;
